import {trans} from '@frontend/helpers'
import type {ChatMessageData} from '@frontend/modules/private/chats/ChatsModuleTypes'
import type {AnyObject} from '@frontend/types/AppTypes'
import {
    mdiCameraOutline,
    mdiSendCircle,
} from '@mdi/js'
import {useAutogrowOnTextArea} from '@shared/helpers/useAutogrowOnTextArea'
import {
    type ChangeEvent,
    type KeyboardEvent,
    useState,
} from 'react'
import {FilePickerInput} from 'swayok-react-mdb-ui-kit/components/FilesPicker/FilePickerInput'
import {FilePickerTrigger} from 'swayok-react-mdb-ui-kit/components/FilesPicker/FilePickerTrigger'
import type {FilePickerFileInfo} from 'swayok-react-mdb-ui-kit/components/FilesPicker/FilePickerTypes'
import {
    IconButton,
    type IconButtonProps,
} from 'swayok-react-mdb-ui-kit/components/Icon/IconButton'
import {Input} from 'swayok-react-mdb-ui-kit/components/Input/Input'
import {handleErrorResponse} from 'swayok-react-mdb-ui-kit/helpers/api/ApiRequestErrorHelpers'
import {runTasksSynchronously} from 'swayok-react-mdb-ui-kit/helpers/task_queue/runTasksSynchronously'
import {useEventCallback} from 'swayok-react-mdb-ui-kit/helpers/useEventCallback'
import type {ApiError} from 'swayok-react-mdb-ui-kit/services/ApiRequestService'
import {ToastService} from 'swayok-react-mdb-ui-kit/services/ToastService'
import {AnalyticsEventReporter} from '../../../../helpers/AnalyticsEventReporter'
import {ChatsApi} from '../ChatsApi'

interface Props {
    adId?: number | null
    executorId?: number | null
    chatId: number
    disabled?: boolean
    canSendImages: boolean
    onNewMessageAdded: (message: ChatMessageData) => void
    // Вызывается при взаимодействии с формой (focus/blur/change поля ввода, нажатие на кнопки).
    onFocus?: () => void
}

// Форма отправки сообщения в чат.
export function ChatMessageForm(props: Props) {

    const {
        adId,
        executorId,
        chatId,
        disabled = false,
        canSendImages,
        onNewMessageAdded,
        onFocus,
    } = props

    // Сообщение.
    const [
        newMessage,
        setNewMessage,
    ] = useState<string>('')

    // Состояние отправки сообщения.
    const [
        isProcessing,
        setIsProcessing,
    ] = useState<boolean>(false)

    // Работа с авто-высотой текстового поля ввода.
    const {
        inputRefHandler: commentTextareaRefHandler,
        update: updateCommentTextareaHeight,
    } = useAutogrowOnTextArea({
        value: newMessage,
    })

    // Отправить сообщение.
    const sendMessage = useEventCallback((): void => {
        onFocus?.()
        if (isProcessing || newMessage.trim() === '') {
            return
        }
        setIsProcessing(true)
        ChatsApi.addMessageToChat(
            newMessage.trim(),
            chatId,
            adId,
            executorId
        )
            .then((messageData: ChatMessageData): void => {
                onNewMessageAdded(messageData)
                setNewMessage('')
                AnalyticsEventReporter.chatMessageSent(
                    chatId,
                    messageData.user.id,
                    adId,
                    executorId
                )
            })
            .catch((error: ApiError) => {
                handleErrorResponse(error, {
                    suppressValidationErrorMessage: true,
                    validationErrors(errors: AnyObject<string>): void {
                        const messages: string[] = Object.values(errors)
                        ToastService.error(
                            messages.join('; ').replace(/\.;/g, ';'),
                            messages.length > 2 ? 8000 : 5000
                        )
                    },
                })
            })
            .finally(() => {
                setIsProcessing(false)
            })
    })

    // Отправить файл.
    const sendFile = useEventCallback(
        (file: File): Promise<void> => ChatsApi.addFileToChat(
            file,
            '',
            chatId,
            adId,
            executorId
        )
            .then((messageData: ChatMessageData): void => {
                onNewMessageAdded(messageData)
            })
            .catch((error: ApiError) => {
                handleErrorResponse(error, {
                    suppressValidationErrorMessage: true,
                    validationErrors(errors: AnyObject<string>): void {
                        const messages: string[] = Object.values(errors)
                        ToastService.error(
                            messages.join('; ').replace(/\.;/g, ';'),
                            messages.length > 2 ? 8000 : 5000
                        )
                    },
                })
            })
    )

    // Обработка прикрепления файлов к чату.
    const onFilesAttached = useEventCallback((
        files: FilePickerFileInfo[]
    ): void => {
        if (isProcessing) {
            return
        }
        const tasks: (() => Promise<void>)[] = []
        for (const item of files) {
            if (item.isNew && !item.error) {
                tasks.push(() => sendFile(item.file))
            }
        }
        if (tasks.length > 0) {
            setIsProcessing(true)
            runTasksSynchronously(tasks)
                .then(() => {
                    setNewMessage('')
                })
                .catch(() => {
                })
                .finally(() => {
                    setIsProcessing(false)
                })
        }
    })

    // Обработка нажатия клавиши Enter c разными модификаторами.
    const onKeyDown = useEventCallback((
        event: KeyboardEvent<HTMLTextAreaElement>
    ): void => {
        if (event.key === 'Enter' && !event.ctrlKey && !event.shiftKey) {
            sendMessage()
            event.preventDefault()
            return
        }
        if (event.key === 'Enter' && (event.ctrlKey || event.shiftKey)) {
            event.preventDefault()
            event.stopPropagation()
            setNewMessage(value => value + '\n')
            event.currentTarget.value += '\n'
            try {
                event.currentTarget.scrollBy({top: 26, left: 0, behavior: 'smooth'})
                updateCommentTextareaHeight()
            } catch (error) {
                console.error(error)
                AnalyticsEventReporter.exception(error)
            }
            return
        }
    })

    return (
        <div className="d-flex flex-row align-items-end justify-content-start">
            {canSendImages && (
                <FilePickerInput
                    translations={trans().file_picker}
                    allowFiles={false}
                    maxFiles={10}
                    maxImageSize={1280}
                    convertImagesToJpeg={true}
                    imagesCompression={0.8}
                    dropInvalidFiles
                    disabled={disabled || isProcessing}
                    onChange={onFilesAttached}
                >
                    <div className="me-3 pb-1">
                        <FilePickerTrigger<IconButtonProps>
                            tag={IconButton}
                            path={mdiCameraOutline}
                            size={30}
                            color="gray"
                            className="btn-hover-theme"
                            onClick={() => {
                                onFocus?.()
                            }}
                        />
                    </div>
                </FilePickerInput>
            )}
            <form
                className="m-0 flex-1"
                onSubmit={event => {
                    event.preventDefault()
                    sendMessage()
                }}
            >
                <Input
                    textarea
                    value={newMessage}
                    wrapperClassName="m-0"
                    className="autogrow minimal no-label"
                    style={{
                        maxHeight: '7.31rem',
                    }}
                    placeholder={trans().cabinet.chats.your_message}
                    disabled={disabled || isProcessing}
                    inputRef={commentTextareaRefHandler}
                    onChange={(event: ChangeEvent<HTMLTextAreaElement>): void => {
                        setNewMessage(event.currentTarget.value)
                        onFocus?.()
                    }}
                    onBlur={() => {
                        setNewMessage(newMessage.trim())
                        onFocus?.()
                    }}
                    onKeyDown={onKeyDown}
                    onFocus={onFocus}
                    onClick={onFocus}
                />
            </form>
            <IconButton
                path={mdiSendCircle}
                size={30}
                color="gray"
                className="ms-3 pb-1 btn-hover-theme"
                disabled={disabled || isProcessing || newMessage.trim() === ''}
                onClick={sendMessage}
                tooltip={
                    newMessage.trim() === ''
                        ? trans().cabinet.chats.error.message_text_is_required
                        : undefined
                }
            />
        </div>
    )
}
